import { Divider, Grid, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { Declaration, DeclarationGoods, DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { EucdmCode, HideElementFor, MultiLangCodes, TextInput, Tooltip } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { disableAmendmentField } from '@utils';
import { DeclarationViewGoodHVFormValues } from './validateDeclarationViewGoodHVForm';

interface DeclarationViewGoodHVGlobalProps {
  form: UseFormReturnType<DeclarationViewGoodHVFormValues>;
  goodDetails: DeclarationGoods;
  declaration: Declaration;
  editDisabled?: boolean;
}

export const DeclarationViewGoodHVGlobal: React.FC<DeclarationViewGoodHVGlobalProps> = ({ form, goodDetails, declaration, editDisabled }) => {
  const { data: settings } = useSettings();
  const codelistSystems = useCodeListSystem({
    group: declaration?.generalInfo?.group,
    isExport: declaration?.generalInfo?.isExport,
  });
  const declarationGroup = declaration?.generalInfo?.group as DeclarationHighValueGroupType;

  return (
    <Stack mx="xl" mb="xl">
      <Stack mb="xl">
        <Title order={5} fw={600}>
          Commodity
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.firstItemData.invoiceDescription">
              <TextInput
                {...form.getInputProps('goodShipmentGlobal.firstItemData.invoiceDescription')}
                label="Overrule Description of Goods"
                disabled={editDisabled}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.firstItemData.typeOfGoods">
              <TextInput {...form.getInputProps('goodShipmentGlobal.firstItemData.typeOfGoods')} label="Type of goods" disabled={editDisabled} />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.firstItemData.cusCode">
              <MultiLangCodes
                {...form.getInputProps('goodShipmentGlobal.firstItemData.cusCode')}
                label="CUS code"
                code="CL016"
                disabled={editDisabled}
                system={codelistSystems}
              />
            </EucdmCode>
          </Grid.Col>
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL}>
            <Grid.Col span={6}>
              <EucdmCode path="otherDataElements.quotaOrderNumber">
                <TextInput {...form.getInputProps('otherDataElements.quotaOrderNumber')} label="Quota order number" disabled={editDisabled} />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
        </Grid>
      </Stack>

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Commodity code
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.firstItemData.hsCode">
              <TextInput name="hsCode" label="HS Code" value={goodDetails.commodityCode.hsCode} disabled />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              {...form.getInputProps('goodShipmentGlobal.firstItemData.hsCode')}
              label="Overrule HS Code"
              disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.firstItemData.taricAdditionalCode">
              <TextInput
                {...form.getInputProps('goodShipmentGlobal.firstItemData.taricAdditionalCode')}
                label="Taric Additional Code 1"
                disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              {...form.getInputProps('goodShipmentGlobal.firstItemData.taricAdditionalCode2')}
              label="Taric Additional Code 2"
              disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.firstItemData.nationalAdditionalCode">
              <TextInput
                {...form.getInputProps('goodShipmentGlobal.firstItemData.nationalAdditionalCode')}
                label="National Additional Code 1"
                disabled={editDisabled}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              {...form.getInputProps('goodShipmentGlobal.firstItemData.nationalAdditionalCode2')}
              label="National Additional Code 2"
              disabled={editDisabled}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <TextInput
              {...form.getInputProps('goodShipmentGlobal.firstItemData.nationalAdditionalCode3')}
              label="National Additional Code 3"
              disabled={editDisabled}
            />
          </Grid.Col>
        </Grid>
      </Stack>

      <Divider mb="md" />

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Packaging
        </Title>

        <Grid>
          <Grid.Col span={12}>
            <EucdmCode path="goodsShipmentItems.packaging.shippingMarks">
              <TextInput label="Shipping marks" value={goodDetails.packaging.shippingMarks} disabled />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodsShipmentItems.packaging.typeOfPackages">
              <TextInput name="typeOfPackage" label="Package type" value={goodDetails.packaging.typeOfPackages} disabled />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={5}>
            <EucdmCode path="goodsShipmentItems.packaging.numberOfPackages">
              {['CT', 'PX', 'PK'].includes(goodDetails.packaging.typeOfPackages) ? (
                <TextInput label="Package quantity" type="number" value={declaration?.goodShipmentGlobal?.totalNumberOfPackages} disabled />
              ) : (
                <TextInput label="Package quantity" type="number" value={declaration?.goodShipmentGlobal?.totalQuantity} disabled />
              )}
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={1} pt={20}>
            {['CT', 'PX', 'PK'].includes(goodDetails.packaging.typeOfPackages) ? (
              <Tooltip content="For this package type, the package quantity relates to the total number of packages for all goods items" />
            ) : (
              <Tooltip content="For this package type, the package quantity relates to the total quantity for all goods items" />
            )}
          </Grid.Col>
        </Grid>
      </Stack>

      <Divider mb="md" />
    </Stack>
  );
};
